import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { CreditCard, Save, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

export const PaymentMarkupCard = () => {
  const [setting, setSetting] = useState(null);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/settings/payment-markup`);
      setSetting(data);
      setValue(String(data.percent));
    } catch (e) {
      toast.error([401, 403].includes(e.response?.status) ? 'Admin login required' : 'Failed to load payment markup');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    const pct = parseFloat(value);
    if (Number.isNaN(pct) || pct < 0 || pct > 25) {
      toast.error('Enter a percentage between 0 and 25');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/settings/payment-markup`, { percent: pct });
      toast.success(`Payment markup set to ${pct}% — live on all new searches`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const preview = (base) => (base * (1 + (parseFloat(value) || 0) / 100)).toFixed(2);

  return (
    <Card className="border-2 border-orange-200" data-testid="payment-markup-card">
      <CardHeader className="bg-orange-50">
        <CardTitle className="flex items-center justify-between text-orange-700">
          <span className="flex items-center gap-2"><CreditCard className="h-5 w-5" /> Payment Processing Markup</span>
          {setting && (
            <Badge className="bg-orange-600 text-white" data-testid="payment-markup-current">{setting.percent}% active</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
        <div>
          <Label htmlFor="payment-markup-input" className="text-sm text-slate-600">
            Silently added to every flight, hotel, visa and transport price (customers never see it as a fee)
          </Label>
          <div className="flex items-center gap-3 mt-2">
            <Input
              id="payment-markup-input"
              data-testid="payment-markup-input"
              type="number"
              step="0.1"
              min="0"
              max="25"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="w-32 text-lg font-bold"
            />
            <span className="text-lg font-semibold text-slate-700">%</span>
            <span className="text-sm text-slate-500">e.g. £1,000 fare → £{preview(1000)}</span>
          </div>
          {setting?.updated_at && (
            <p className="text-xs text-slate-400 mt-2">Last changed {new Date(setting.updated_at).toLocaleString('en-GB')} by {setting.updated_by}</p>
          )}
        </div>
        <Button onClick={save} disabled={saving || !setting} className="bg-orange-600 hover:bg-orange-700" data-testid="payment-markup-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save markup
        </Button>
      </CardContent>
    </Card>
  );
};
